import Dexie, { type Table } from 'dexie'
import type { Member } from '@/models/member.model'
import type { Expense } from '@/models/expense.model'
import type { Income } from '@/models/income.model'
import type { Bill } from '@/models/bill.model'
import type { Budget } from '@/models/budget.model'
import type { SavingsGoal } from '@/models/savings-goal.model'
import type { GoalContribution } from '@/models/goal-contribution.model'
import type { Task } from '@/models/task.model'
import type { Subtask } from '@/models/subtask.model'
import type { GroceryItem } from '@/models/grocery.model'
import type { InventoryItem } from '@/models/inventory.model'
import type { Reminder } from '@/models/reminder.model'
import type { Note } from '@/models/note.model'
import type { WishlistItem } from '@/models/wishlist.model'
import type { JournalEntry } from '@/models/journal.model'
import type { Contact } from '@/models/contact.model'
import type { HouseholdDocument } from '@/models/document.model'
import type { Board, BoardItem } from '@/models/board.model'
import type { ExpenseSplit } from '@/models/expense-split.model'
import type { CalendarEvent } from '@/models/calendar-event.model'
import type { EntityShare } from '@/models/entity-share.model'
import type { Tracker, TrackerEntry } from '@/models/tracker.model'
import type { ManualSettlement } from '@/models/manual-settlement.model'

class SteadDatabase extends Dexie {
  members!: Table<Member, string>
  expenses!: Table<Expense, string>
  expense_splits!: Table<ExpenseSplit, string>
  income!: Table<Income, string>
  bills!: Table<Bill, string>
  budgets!: Table<Budget, string>
  savings_goals!: Table<SavingsGoal, string>
  goal_contributions!: Table<GoalContribution, string>
  tasks!: Table<Task, string>
  subtasks!: Table<Subtask, string>
  groceries!: Table<GroceryItem, string>
  inventory!: Table<InventoryItem, string>
  reminders!: Table<Reminder, string>
  notes!: Table<Note, string>
  wishlists!: Table<WishlistItem, string>
  journal_entries!: Table<JournalEntry, string>
  contacts!: Table<Contact, string>
  documents!: Table<HouseholdDocument, string>
  boards!: Table<Board, string>
  board_items!: Table<BoardItem, string>
  calendar_events!: Table<CalendarEvent, string>
  entity_shares!: Table<EntityShare, string>
  trackers!: Table<Tracker, string>
  tracker_entries!: Table<TrackerEntry, string>
  manual_settlements!: Table<ManualSettlement, string>

  constructor() {
    super('stead-db')
    this.version(1).stores({
      members: 'id, household_id',
      expenses: 'id, household_id, date, category',
      income: 'id, household_id, date',
      bills: 'id, household_id, status',
      budgets: 'id, household_id, month',
      savings_goals: 'id, household_id, status',
      goal_contributions: 'id, goal_id',
      tasks: 'id, household_id, status, due_date, assignee',
      subtasks: 'id, task_id',
      groceries: 'id, household_id, status, category',
      inventory: 'id, household_id, stock_status',
      reminders: 'id, household_id, due_date, status',
      notes: 'id, household_id, pinned',
      maintenance: 'id, household_id, status, next_due_date',
    })
    this.version(2).stores({
      tasks: 'id, household_id, status, due_date, assignee, task_type',
      maintenance: null, // remove maintenance table
    })
    this.version(3).stores({
      expenses: 'id, household_id, date, category, scope, owner_id',
      income: 'id, household_id, date, scope, owner_id',
      budgets: 'id, household_id, month, scope, owner_id',
      savings_goals: 'id, household_id, status, scope, owner_id',
      goal_contributions: 'id, goal_id, scope, owner_id',
      tasks: 'id, household_id, status, due_date, assignee, task_type, scope, owner_id',
      notes: 'id, household_id, pinned, scope, owner_id',
    })
    this.version(4).stores({
      wishlists: 'id, household_id, owner_id, status',
      journal_entries: 'id, household_id, owner_id, entry_date',
      contacts: 'id, household_id',
      documents: 'id, household_id, doc_type',
    })
    this.version(5).stores({
      expense_splits: 'id, expense_id, household_id, member_id, settled',
    })
    this.version(6).stores({
      calendar_events: 'id, household_id, start_date, scope, owner_id',
    })
    this.version(7).stores({
      entity_shares: 'id, entity_type, entity_id, shared_with, household_id',
      calendar_events: 'id, household_id, start_date, scope, owner_id, visibility',
      notes: 'id, household_id, pinned, scope, owner_id, visibility',
      tasks: 'id, household_id, status, due_date, assignee, task_type, scope, owner_id, visibility',
      wishlists: 'id, household_id, owner_id, status, visibility',
    })
    this.version(8).stores({
      subscriptions: null,
      habits: null,
      habit_logs: null,
      meal_plans: null,
      meals: null,
      boards: 'id, household_id, owner_id, scope, position',
      board_items: 'id, board_id, household_id, group_name, position',
    })
    this.version(9).stores({
      trackers: 'id, household_id, owner_id, scope, position, value_type',
      tracker_entries: 'id, tracker_id, household_id, entry_date',
    })
    this.version(10).stores({
      expenses: 'id, household_id, date, category, scope, owner_id, paid_by, paid_by_type',
      manual_settlements: 'id, household_id, owner_id, visibility, status, date, counterparty_member_id',
    })
  }
}

export const db = new SteadDatabase()
